import * as React from "react";

import { cn } from "../../lib/utils.js";

/**
 * A placeholder block for content that has not loaded.
 *
 * The pulse itself lives in tokens.css (`.skeleton`), which also turns it off
 * under prefers-reduced-motion. Callers only supply the shape via `className`,
 * so a skeleton always matches the footprint of what replaces it — the point of
 * a skeleton is that nothing moves when the real content arrives.
 *
 * Always aria-hidden: a screen reader should hear the live region announce
 * "loading", not a tree of meaningless boxes.
 */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      aria-hidden="true"
      className={cn("skeleton block rounded-md bg-surface-200", className)}
      {...props}
    />
  );
}

/**
 * Wraps a skeleton tree so assistive tech hears one "Loading" rather than
 * nothing at all. `label` names what is loading, e.g. "Loading attendance".
 */
export function SkeletonRegion({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
